import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";

import { getUnits } from "utils";
import { selectAPIKey, selectPropertyId, setIsUnitTourScreen, unitSliceData } from "slices";
import { setUnitFilters } from "slices/units-slice/index";
import { HeaderBarComp, HylyLoader } from "components";

import TopBar from "./TopBar";
import ShowUnits from "./ShowUnits";
import { budgetFormat, getPriceRange, isValidDateRange } from "./utils";

const UnitTourBox = () => {
  const dispatch = useDispatch();

  const filterRef = useRef(null);

  const { units, filters, budgets } = useSelector(unitSliceData);
  const propertyId = useSelector(selectPropertyId);
  const apiKey = useSelector(selectAPIKey);
  const [isVisible, setIsVisible] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState(filters || null);
  const [tableData, setTableData] = useState([]);
  const [allTableData, setAllTableData] = useState([]);
  const [currentFloorPlans, setCurrentFloorPlans] = useState([]);
  const [desiredMoveInDate, setDesiredMoveInDate] = useState("");

  useEffect(() => {
    if (filters) {
      const { visible, ...filter } = filters;

      if (!!visible) {
        setIsVisible(true);
      }

      if (filter?.budgets?.includes("-")) {
        let myValue = filter?.budgets?.split("-");
        const result = budgets?._form_desired_price_range?.find((item) => item.value[0] == myValue[0] && item.value[1] == myValue[1])?.Display;
        filter.budgets = result || budgetFormat(myValue);
      } else if (filter.budgets && typeof filter.budgets != "object") {
        filter.budgets = budgetFormat([budgets?._custom_price_range[0], filter.budgets]);
      }

      setFilterLoading(true);
      const desiredMoveIn = filterData["moveIns"];

      if (desiredMoveIn?.includes("..")) {
        setFilterData(() => {
          return { ...filter, isAsap: true };
        });
      } else {
        setFilterData(filter);
      }

      setTimeout(() => {
        setFilterLoading(false);
      }, 100);
    }
  }, [filters]);

  useEffect(() => {
    const allUnits =
      units?.floor_plans?.flatMap((i) =>
        (i.units || []).map((unit) => ({
          ...unit,
          fp_id: i.id,
          fp_name: i.fp_name,
          unit_floor_plan_image: i.fp_image,
        }))
      ) || [];
    setCurrentFloorPlans(units?.floor_plans?.map((i) => ({ id: i.id, name: i.fp_name })) || []);
    setTableData(allUnits || []);
    setAllTableData(allUnits || []);
    filterByFloorPlan(allUnits, filterData);
  }, [units]);

  const filterByFloorPlan = (data, filter) => {
    if (!filter?.allFloorPlans?.id) {
      setTableData(data || []);
    } else {
      const allUnits = data?.filter((i) => i.fp_id === filter?.allFloorPlans?.id);
      setTableData(allUnits || []);
    }
  };

  const getFilteredUnits = (filter) => {
    setLoading(true);
    let queryParams;

    if (!filter) {
      queryParams = `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}}`;
    } else {
      const budgets = getPriceRange(filter.budgets) || null;

      const length = budgets?.length || 0;

      const desiredMoveIn = filter["moveIns"];

      if (desiredMoveIn) {
        setDesiredMoveInDate(desiredMoveIn);
      }

      const min = length === 0 ? null : length > 1 ? budgets[0] : 0;
      const max = length === 0 ? null : length > 1 ? budgets[1] : budgets[0];

      let convertedDate = desiredMoveIn;
      let isRange = false;

      if (moment(desiredMoveIn, "MMM DD, YYYY", true).isValid()) {
        convertedDate = moment(desiredMoveIn, "MMM DD, YYYY").format("YYYY-MM-DD");
      }

      if (isValidDateRange(desiredMoveIn)) {
        isRange = true;
        convertedDate = desiredMoveIn.split(",")?.join("..");
      }

      queryParams = `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}${filter.bedrooms ? `&bedroom_type=${filter.bedrooms}` : ""}${
        !desiredMoveIn || desiredMoveIn === "any"
          ? ""
          : `&desired_move_in=${filter?.isAsap || !isRange ? "..." + convertedDate || desiredMoveIn : convertedDate || desiredMoveIn}`
      }${min ? `&min_price=${min}` : ""}${max ? `&max_price=${max}` : ""}`;
    }

    getUnits(queryParams)
      .then((res) => {
        const allUnits =
          res?.data?.floor_plans?.flatMap((i) =>
            (i.units || []).map((unit) => ({
              ...unit,
              fp_id: i.id,
              fp_name: i.fp_name,
              unit_floor_plan_image: i.fp_image,
            }))
          ) || [];

        if (res?.data.search_result === false) {
          setIsVisible(true);
        }

        let floors = res?.data?.floor_plans?.map((i) => ({ id: i.id, name: i.fp_name, type: i.fp_type })) || [];

        if (filter?.bedrooms && res?.data.search_result === false) {
          floors = floors?.filter((i) => i.type == filter?.bedrooms);
        }

        const isSelectedFloorPresent = floors.some((floor) => floor.id === filter?.allFloorPlans?.id && floor.name === filter?.allFloorPlans?.name);

        setCurrentFloorPlans(floors || []);
        setTableData(allUnits || []);
        setAllTableData(allUnits || []);

        if (isSelectedFloorPresent) {
          filterByFloorPlan(allUnits, filter);
        } else {
          setFilterData((p) => ({
            ...p,
            allFloorPlans: {
              id: null,
              name: "All",
            },
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const closeUnitTourScreen = () => {
    dispatch(setIsUnitTourScreen(false));
    dispatch(setUnitFilters(null));
  };

  return (
    <HeaderBarComp title={"Available Units"} onBackButtonClick={closeUnitTourScreen} search_result={false} isVisible={isVisible} setIsVisible={setIsVisible}>
      {!filterLoading && (
        <TopBar
          filterRef={filterRef}
          filterData={filterData}
          allTableData={allTableData}
          filterByFloorPlan={filterByFloorPlan}
          setFilterData={setFilterData}
          getFilteredUnits={getFilteredUnits}
          currentFloorPlans={currentFloorPlans}
          unitsCount={tableData.length || "No"}
          desiredMoveInDate={desiredMoveInDate}
          setDesiredMoveInDate={setDesiredMoveInDate}
        />
      )}
      {loading ? (
        <HylyLoader size="2rem" containerStyle={{ marginTop: "5rem" }} />
      ) : (
        <ShowUnits filterRef={filterRef} tableData={tableData} setFilterData={setFilterData} />
      )}
    </HeaderBarComp>
  );
};

export default UnitTourBox;
